import React from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Autocomplete, Chip, CircularProgress, Divider, TextField, Tooltip } from '@mui/material';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import {
  AUTH_TYPE_OPTIONS,
  CONTROL_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from './customerAuthFilterConstants';

const iconStyles = { position: 'absolute', right: 12, top: 14 };
const multiSelectCompactSx = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    flexWrap: 'nowrap',
    minHeight: 48,
  },
};
const renderTags = value => {
  const first = value[0];
  const rest = value.length - 1;
  return (
    <>
      <span
        style={{
          display: 'inline-block',
          maxWidth: 180,
          overflow: 'hidden',
          padding: '5px',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {first.label}
      </span>
      {rest > 0 && <Chip label={`+${rest}`} size="small" variant="autocomplete" />}
    </>
  );
};
const renderInputWithSpinner = (label, loading, optionsLength) => params =>
  (
    <TextField
      {...params}
      label={label}
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'secondary.main' }} />
              ) : !loading && !optionsLength ? (
                <Tooltip title="No options available">
                  <InfoRoundedIcon sx={{ color: 'graphic.main', ...iconStyles }} />
                </Tooltip>
              ) : null}
              {!!optionsLength && params.InputProps.endAdornment}
            </>
          ),
        },
      }}
    />
  );
const getOptionLabel = option => option?.label || '';
const isValueOptionEqual = (option, value) => option?.value === value?.value;
const CustomerAuthorizationsFilters = ({
  affiliateOptions,
  authTypes,
  controlTypes,
  locationOptions,
  isExporting,
  locationsLoading,
  onAffiliateChange,
  onAuthTypeChange,
  onControlTypeChange,
  onLocationChange,
  onProductChange,
  onStatusChange,
  productOptions,
  productsLoading,
  selectedAffiliate,
  selectedLocations,
  selectedProducts,
  showAffiliateFilter,
  status,
}) => (
  <>
    {showAffiliateFilter && (
      <Autocomplete
        autoHighlight
        disableClearable
        disabled={isExporting}
        disablePortal
        getOptionLabel={getOptionLabel}
        id="customer-auth-affiliate-filter"
        isOptionEqualToValue={isValueOptionEqual}
        onChange={(_, newValue) => onAffiliateChange(newValue)}
        options={affiliateOptions}
        renderInput={params => <TextField {...params} label="Affiliate" />}
        value={selectedAffiliate}
      />
    )}
    {showAffiliateFilter && <Divider />}
    {/* Locations — multi-select */}
    <Autocomplete
      autoHighlight
      disableCloseOnSelect
      disabled={isExporting}
      disablePortal
      getOptionLabel={getOptionLabel}
      id="customer-auth-location-filter"
      isOptionEqualToValue={isValueOptionEqual}
      loading={locationsLoading}
      multiple
      onChange={(_, newValue) => onLocationChange(newValue)}
      openOnFocus
      options={locationOptions}
      renderInput={renderInputWithSpinner('Locations', locationsLoading, locationOptions.length)}
      renderTags={renderTags}
      sx={multiSelectCompactSx}
      value={selectedLocations}
    />
    {/* Products — multi-select */}
    <Autocomplete
      autoHighlight
      disableCloseOnSelect
      disabled={isExporting}
      disablePortal
      getOptionLabel={getOptionLabel}
      id="customer-auth-product-filter"
      isOptionEqualToValue={isValueOptionEqual}
      loading={productsLoading}
      multiple
      onChange={(_, newValue) => onProductChange(newValue)}
      openOnFocus
      options={productOptions}
      renderInput={renderInputWithSpinner('Products', productsLoading, productOptions.length)}
      renderTags={renderTags}
      sx={multiSelectCompactSx}
      value={selectedProducts}
    />
    {/* Control Type — multi-select */}
    <Autocomplete
      autoHighlight
      disableCloseOnSelect
      disabled={isExporting}
      disablePortal
      getOptionLabel={getOptionLabel}
      id="customer-auth-control-type-filter"
      isOptionEqualToValue={isValueOptionEqual}
      multiple
      onChange={(_, newValue) => onControlTypeChange(newValue)}
      openOnFocus
      options={CONTROL_TYPE_OPTIONS}
      renderInput={params => <TextField {...params} label="Control Type" />}
      renderTags={renderTags}
      sx={multiSelectCompactSx}
      value={controlTypes}
    />
    {/* Auth Type — multi-select */}
    <Autocomplete
      autoHighlight
      disableCloseOnSelect
      disabled={isExporting}
      disablePortal
      getOptionLabel={getOptionLabel}
      id="customer-auth-type-filter"
      isOptionEqualToValue={isValueOptionEqual}
      multiple
      onChange={(_, newValue) => onAuthTypeChange(newValue)}
      openOnFocus
      options={AUTH_TYPE_OPTIONS}
      renderInput={params => <TextField {...params} label="Authorization Type" />}
      renderTags={renderTags}
      sx={multiSelectCompactSx}
      value={authTypes}
    />
    {/* Status — single-select */}
    <Autocomplete
      autoHighlight
      disableClearable
      disabled={isExporting}
      disablePortal
      getOptionLabel={getOptionLabel}
      id="customer-auth-status-filter"
      isOptionEqualToValue={isValueOptionEqual}
      onChange={(_, newValue) => onStatusChange(newValue?.value)}
      options={STATUS_OPTIONS}
      renderInput={params => <TextField {...params} label="Status" />}
      value={STATUS_OPTIONS.find(option => option.value === status) || STATUS_OPTIONS[0]}
    />
  </>
);
CustomerAuthorizationsFilters.propTypes = {
  affiliateOptions: arrayOf(shape({ label: string, value: string })),
  authTypes: arrayOf(shape({ label: string, value: string })),
  controlTypes: arrayOf(shape({ label: string, value: string })),
  isExporting: bool,
  locationOptions: arrayOf(shape({ label: string })),
  locationsLoading: bool,
  onAffiliateChange: func.isRequired,
  onAuthTypeChange: func.isRequired,
  onControlTypeChange: func.isRequired,
  onLocationChange: func.isRequired,
  onProductChange: func.isRequired,
  onStatusChange: func.isRequired,
  productOptions: arrayOf(shape({ label: string })),
  productsLoading: bool,
  selectedAffiliate: shape({ label: string, value: string }),
  selectedLocations: arrayOf(shape({ label: string })),
  selectedProducts: arrayOf(shape({ label: string })),
  showAffiliateFilter: bool,
  status: string,
};
CustomerAuthorizationsFilters.defaultProps = {
  affiliateOptions: [],
  authTypes: [],
  controlTypes: [],
  isExporting: false,
  locationOptions: [],
  locationsLoading: false,
  productOptions: [],
  productsLoading: false,
  selectedAffiliate: null,
  selectedLocations: [],
  selectedProducts: [],
  showAffiliateFilter: true,
  status: STATUS_OPTIONS[0].value,
};
export default CustomerAuthorizationsFilters;
